import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Search, ArrowRight, X,
  CornerDownLeft, Users, Hand, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLeads, LeadsStore } from '../lib/leadsStore';
import { useTeam, TeamStore } from '../lib/teamStore';
import { addressIncludes, addressStartsWith, normalizeAddress } from '../lib/addressMatch';
import { cn } from '../lib/utils';
import { useAuth } from '../lib/authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ------------------------------------------------------------------ */
/* SearchResults — global cross-LO search page.                       */
/*                                                                    */
/* Empty query  → tall hero banner + suggestion chips + recent leads. */
/* Query active → hero collapses to a slim header bar so the dense    */
/* results table dominates the viewport (power-user pattern, faster   */
/* at 50+ results than chunky cards).                                 */
/* ------------------------------------------------------------------ */
export default function SearchResults() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const leads = useLeads();
  useTeam();

  const urlQ = searchParams.get('q') || '';
  const [q, setQ] = useState(urlQ);
  const inputRef = useRef(null);

  useEffect(() => { setQ(urlQ); }, [urlQ]);
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    const v = q.trim();
    const ps = new URLSearchParams(searchParams);
    if (v) ps.set('q', v); else ps.delete('q');
    setSearchParams(ps, { replace: true });
  };

  // Server-side search — kicks in for every non-empty query so we can find
  // leads that aren't in the top-500 in-memory cache (the cache holds the
  // highest-scored 500 only; older / lower-priority leads — including
  // mailer-imported leads with reference IDs like "C0528-0110" — only live
  // server-side). Merged with the local cache so cached hits show instantly
  // and server matches appear once the round-trip resolves (~150-300ms).
  const [serverHits, setServerHits] = useState([]);
  const [serverLoading, setServerLoading] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const query = urlQ.trim();
    if (!query) {
      setServerHits([]);
      return;
    }
    let cancelled = false;
    setServerLoading(true);
    setServerError(false);
    axios
      .get(`${BACKEND_URL}/api/leads`, { params: { q: query, light: true, limit: 200 }, timeout: 15000 })
      .then((r) => {
        if (cancelled) return;
        setServerHits(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => { if (!cancelled) { setServerHits([]); setServerError(true); } })
      .finally(() => { if (!cancelled) setServerLoading(false); });
    return () => { cancelled = true; };
  }, [urlQ, retry]);

  const results = useMemo(() => {
    const query = urlQ.trim().toLowerCase();
    if (!query) return [];
    const digits = query.replace(/\D/g, '');
    // Merge: cached leads + server hits, dedupe by id, then score.
    const byId = new Map();
    for (const l of leads) byId.set(l.id, l);
    for (const l of serverHits) if (!byId.has(l.id)) byId.set(l.id, l);
    return Array.from(byId.values())
      .map((l) => ({ l, score: scoreLead(l, query, digits) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.l);
  }, [urlQ, leads, serverHits]);

  const hasQuery = urlQ.trim() !== '';

  return (
    <div className="space-y-5">
      {/* Hero — full when empty, collapsed when there are results. */}
      {hasQuery ? (
        <SlimHero
          q={q}
          setQ={setQ}
          submit={submit}
          inputRef={inputRef}
          matchCount={results.length}
          urlQ={urlQ}
        />
      ) : (
        <TallHero q={q} setQ={setQ} submit={submit} inputRef={inputRef} />
      )}

      {/* Body */}
      {!hasQuery ? (
        <EmptyState />
      ) : results.length === 0 ? (
        serverLoading ? <SearchingState query={urlQ} />
          : serverError ? <SearchErrorState onRetry={() => setRetry((n) => n + 1)} />
          : <NoMatchState query={urlQ} />
      ) : (
        <ResultsTable results={results} query={urlQ} onOpen={(id) => navigate(`/leads/${id}`)} />
      )}
    </div>
  );
}

/* ---- Hero variants ---- */

function TallHero({ q, setQ, submit, inputRef }) {
  return (
    <section className="relative overflow-hidden rounded-3xl bg-neutral-900 text-white p-8 md:p-10 noise">
      <div className="absolute inset-0 bg-dot-dark opacity-40 pointer-events-none" />
      <div className="absolute -top-20 -right-12 h-64 w-64 rounded-full bg-[hsl(var(--kf-gold))]/15 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="text-[10.5px] uppercase tracking-[0.32em] text-[hsl(var(--kf-gold))]/80 font-bold">Global Search</div>
        <h1 className="display text-[36px] md:text-[44px] font-bold tracking-tight mt-2 leading-[1.05]">
          Find a client across <span className="text-[hsl(var(--kf-gold))]">every loan officer's</span> book.
        </h1>
        <p className="text-[13.5px] text-white/60 mt-2 max-w-[640px]">
          First name, last name, phone, email, property address — searches all 17 LO pipelines in one shot.
        </p>

        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2 max-w-[720px]">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 sm:h-5 sm:w-5 text-white/40 pointer-events-none" />
            <input
              ref={inputRef}
              data-testid="search-input"
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck={false}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search clients…"
              className="w-full h-16 sm:h-14 pl-12 pr-4 rounded-xl bg-white/10 border border-white/15 text-[17px] sm:text-[15px] font-semibold text-white placeholder:font-normal placeholder:text-white/40 focus:outline-none focus:border-[hsl(var(--kf-gold))] focus:bg-white/15 transition"
            />
          </div>
          <button
            type="submit"
            data-testid="search-submit"
            className="h-14 px-5 rounded-xl bg-[hsl(var(--kf-gold))] text-neutral-900 text-[14px] sm:text-[13px] font-bold hover:bg-[hsl(var(--kf-gold-deep))] hover:text-white transition flex items-center justify-center gap-2"
          >
            Search
            <CornerDownLeft className="h-4 w-4" />
          </button>
        </form>
      </div>
    </section>
  );
}

function SlimHero({ q, setQ, submit, inputRef, matchCount, urlQ }) {
  return (
    <section className="relative rounded-2xl bg-neutral-900 text-white px-5 py-3 flex items-center gap-4 noise">
      <div className="absolute inset-0 bg-dot-dark opacity-30 pointer-events-none" />
      <div className="relative flex items-center gap-2 shrink-0">
        <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.28em] font-bold text-[hsl(var(--kf-gold))]/90">
          <Users className="h-3 w-3" /> Global
        </span>
      </div>

      <form onSubmit={submit} className="relative flex-1 max-w-[560px] flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
          <input
            ref={inputRef}
            data-testid="search-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search clients…"
            className="w-full h-10 pl-10 pr-3 rounded-lg bg-white/10 border border-white/15 text-[13.5px] font-semibold text-white placeholder:font-normal placeholder:text-white/40 focus:outline-none focus:border-[hsl(var(--kf-gold))] focus:bg-white/15 transition"
          />
        </div>
        <button
          type="submit"
          data-testid="search-submit"
          className="h-10 px-4 rounded-lg bg-[hsl(var(--kf-gold))] text-neutral-900 text-[12px] font-bold hover:bg-[hsl(var(--kf-gold-deep))] hover:text-white transition"
        >
          Search
        </button>
      </form>

      <div className="relative ml-auto flex items-center gap-3 text-[11.5px] text-white/60">
        <span>
          <span className="mono font-bold text-white">{matchCount}</span>{' '}
          {matchCount === 1 ? 'match' : 'matches'} for <span className="font-semibold text-white">"{urlQ}"</span>
        </span>
      </div>
    </section>
  );
}

/* ---- Results table ---- */

function ResultsTable({ results, query, onOpen }) {
  return (
    <section className="rounded-2xl bg-white border hairline overflow-hidden">
      {/* Header */}
      <div className="hidden md:grid grid-cols-12 gap-x-4 px-5 py-2 text-[10.5px] uppercase tracking-wider text-neutral-700 font-bold border-b hairline bg-neutral-50/60">
        <div className="col-span-3">Name</div>
        <div className="col-span-3">Property address</div>
        <div className="col-span-2">Phone</div>
        <div className="col-span-2">Email</div>
        <div className="col-span-2">Assigned to</div>
      </div>

      <div className="divide-y hairline-soft">
        {results.map((lead) => (
          <ResultRow key={lead.id} lead={lead} query={query} onOpen={() => onOpen(lead.id)} />
        ))}
      </div>
    </section>
  );
}

function ResultRow({ lead, query, onOpen }) {
  const { user } = useAuth();
  const CURRENT_LO_ID = user?.id || null;
  const isAdmin = user?.role === 'admin';
  const app = lead.application || {};
  const assignedToId = lead.assignedTo || null;
  const assignee = assignedToId ? TeamStore.get(assignedToId) : null;
  const isUnassigned = !assignee;
  // Permission gate: LOs cannot peek at unclaimed mailer leads — they must
  // claim ownership first. Admins can always open anything. Once a lead is
  // claimed by ANYONE, every LO can still view it (so they know who owns it
  // and can refer business through the right channel).
  const canOpen = isAdmin || !isUnassigned;
  const addr = app.propertyAddress || app.currentAddress;
  const addrCity = app.propertyCity || app.currentCity;
  const addrState = app.propertyState || app.currentState;
  const addrZip = app.propertyZip || app.currentZip;
  const addrFull = addr ? [addr, [addrCity, addrState].filter(Boolean).join(', ')].filter(Boolean).join(' · ') : '';
  // Paste-ready copy string — clean commas + ZIP so pasting into ARIVE /
  // Google Maps / email templates works with no manual cleanup. Falls back
  // to the display string if any part is missing.
  const addrCopy = addr
    ? [addr, [addrCity, addrState].filter(Boolean).join(', '), addrZip].filter(Boolean).join(', ')
    : '';

  const claim = async (e) => {
    // Don't bubble up to the row's onOpen handler.
    e.preventDefault();
    e.stopPropagation();
    if (!CURRENT_LO_ID) { toast.error('Please sign in to claim leads'); return; }
    const updated = await LeadsStore.update(lead.id, { assignedTo: CURRENT_LO_ID });
    if (updated) {
      const you = TeamStore.get(CURRENT_LO_ID);
      toast.success(`${updated.name} is now yours${you ? ` — assigned to ${you.name}` : ''}.`);
      // Auto-open the lead profile right after a successful claim so the
      // LO drops straight into the workspace they unlocked.
      onOpen?.();
    } else {
      toast.error('Could not claim this lead');
    }
  };

  // Click guard — if the LO doesn't yet have rights to view, surface the
  // claim CTA via a toast instead of silently opening (or worse, exposing)
  // the lead profile.
  const handleRowClick = () => {
    if (!canOpen) {
      toast.message('Claim this lead first to view their profile.', {
        description: 'Click "Claim lead" on this row to take ownership.',
      });
      return;
    }
    onOpen?.();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`search-result-${lead.id}`}
      onClick={handleRowClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick(); } }}
      title={!canOpen ? 'Claim this lead first to view their profile' : undefined}
      className={cn(
        'w-full grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-1 px-5 py-3 items-center text-left transition group cursor-pointer outline-none focus:ring-2 focus:ring-[hsl(var(--kf-gold))]',
        canOpen ? 'hover:bg-neutral-50/60' : 'hover:bg-amber-50/40 cursor-default',
      )}
    >
      {/* Name + avatar. Status (hot/nurture/etc) is intentionally hidden
          here — temp is a private property of the lead's assigned LO. */}
      <div className="col-span-3 flex items-center gap-3 min-w-0">
        <div className="shrink-0 h-8 w-8 rounded-full bg-neutral-900 text-white text-[10.5px] font-bold flex items-center justify-center">
          {lead.initials || (lead.name || '?').slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-[14px] font-bold text-neutral-900 truncate leading-tight">
            <Highlight text={lead.name || ''} q={query} />
          </div>
          {/* Campaign ref + source — small, mono, very obviously secondary.
              Only renders when the lead actually has a referenceId so legacy
              hand-entered leads don't get an awkward "—" line.            */}
          {(lead.referenceId || lead.source) && (
            <div className="flex items-center gap-1.5 mt-0.5 text-[10.5px] text-neutral-500 truncate">
              {lead.referenceId && (
                <span className="mono font-bold text-neutral-700 tabular-nums">
                  <Highlight text={lead.referenceId} q={query} />
                </span>
              )}
              {lead.referenceId && lead.source && <span className="text-neutral-300">·</span>}
              {lead.source && <span className="truncate">{lead.source}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Property address — text is user-selectable so LOs can highlight
          + copy directly. Clicks/mousedowns on the text stop propagating
          so selecting doesn't accidentally trigger the row navigation.
          A hover Copy icon on the right one-taps the full address into
          the clipboard. */}
      <div className="col-span-3 flex items-center gap-1.5 min-w-0 group/addr">
        <div
          className="flex-1 min-w-0 text-[13px] text-neutral-700 truncate select-text cursor-text"
          title={addrFull}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          {addrFull ? <Highlight text={addrFull} q={query} /> : <span className="text-neutral-300">—</span>}
        </div>
        {addrFull && (
          <button
            type="button"
            data-testid={`search-result-${lead.id}-copy-address`}
            onClick={async (e) => {
              e.stopPropagation();
              try {
                await navigator.clipboard.writeText(addrCopy || addrFull);
                toast.success('Address copied');
              } catch {
                toast.error('Could not copy to clipboard');
              }
            }}
            title="Copy address"
            className="shrink-0 h-6 w-6 rounded flex items-center justify-center text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 opacity-0 group-hover/addr:opacity-100 focus:opacity-100 transition"
          >
            <Copy className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Phone */}
      <div
        className="col-span-2 text-[13px] text-neutral-700 truncate tabular-nums select-text cursor-text"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {lead.phone ? <Highlight text={lead.phone} q={query} /> : <span className="text-neutral-300">—</span>}
      </div>

      {/* Email */}
      <div
        className="col-span-2 text-[12.5px] text-neutral-700 truncate select-text cursor-text"
        title={lead.email}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {lead.email ? <Highlight text={lead.email} q={query} /> : <span className="text-neutral-300">—</span>}
      </div>

      {/* Assigned to / Claim */}
      <div className="col-span-2 flex items-center gap-2 min-w-0">
        {isUnassigned ? (
          <span
            role="button"
            tabIndex={0}
            data-testid={`search-result-${lead.id}-claim`}
            onClick={claim}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') claim(e); }}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-neutral-900 text-[hsl(var(--kf-gold))] text-[11.5px] font-bold hover:bg-neutral-800 transition cursor-pointer"
            title="Take ownership of this campaign lead"
          >
            <Hand className="h-3.5 w-3.5" /> Claim lead
          </span>
        ) : (
          <>
            <span className="shrink-0 h-6 w-6 rounded-full bg-neutral-100 text-neutral-700 text-[9.5px] font-bold flex items-center justify-center">
              {assignee.initials || (assignee.name || '').slice(0, 2).toUpperCase()}
            </span>
            <span className="text-[12.5px] font-semibold text-neutral-800 truncate">{assignee.name}</span>
            <ArrowRight className="ml-auto h-3.5 w-3.5 text-neutral-300 group-hover:text-neutral-900 group-hover:translate-x-0.5 transition shrink-0" />
          </>
        )}
      </div>
    </div>
  );
}

/* ---- Highlight + Empty / No-match states ---- */

function Highlight({ text, q }) {
  const s = String(text ?? '');
  const query = String(q ?? '').trim();
  if (!query) return <>{s}</>;
  const idx = s.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{s}</>;
  return (
    <>
      {s.slice(0, idx)}
      <mark className="bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] rounded-sm px-0.5 font-bold">
        {s.slice(idx, idx + query.length)}
      </mark>
      {s.slice(idx + query.length)}
    </>
  );
}

function EmptyState() {
  const leads = useLeads();
  const recent = leads.slice(0, 4);
  // On mobile with no recent leads, render nothing — the hero alone is the
  // entire screen. Avoid an empty white card cluttering the view.
  const hasContent = recent.length > 0;
  return (
    <section className={cn(
      'rounded-2xl border hairline bg-white dark:bg-neutral-900 p-6',
      !hasContent && 'hidden md:block'
    )}>
      {/* "Try searching" chips — desktop/tablet only. On phones the search */}
      {/* hero IS the experience; the chips are just visual clutter. */}
      <div className="hidden md:block">
        <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-neutral-500">Try searching</div>
        <div className="flex flex-wrap gap-2 mt-3">
          {['Loan Tracking ID', 'Phone number', 'Last name', 'Property street', 'City', 'Email'].map((s) => (
            <span key={s} className="inline-flex items-center gap-1 text-[11.5px] font-bold text-neutral-700 dark:text-neutral-300 bg-neutral-50 dark:bg-neutral-800 border hairline rounded-full px-3 py-1.5">
              <Search className="h-3 w-3 text-neutral-400" /> {s}
            </span>
          ))}
        </div>
      </div>
      {recent.length > 0 && (
        <>
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-neutral-500 mt-0 md:mt-6">Recent leads</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
            {recent.map((lead) => (
              <Link
                key={lead.id}
                to={`/leads/${lead.id}`}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-50 group"
              >
                <span className="shrink-0 h-9 w-9 rounded-full bg-neutral-900 text-white text-[10.5px] font-bold flex items-center justify-center">
                  {lead.initials}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-bold text-neutral-900 truncate">{lead.name}</span>
                  <span className="block text-[11px] text-neutral-500 truncate">{lead.city || lead.email}</span>
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-neutral-300 group-hover:text-neutral-900 transition" />
              </Link>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function NoMatchState({ query }) {
  return (
    <section className="rounded-2xl border hairline bg-white p-10 text-center" data-testid="search-no-match">
      <div className="inline-flex h-12 w-12 rounded-2xl bg-neutral-100 text-neutral-500 items-center justify-center mb-4">
        <X className="h-5 w-5" />
      </div>
      <div className="display text-[20px] font-bold text-neutral-900">No clients match &ldquo;{query}&rdquo;</div>
      <div className="text-[13px] text-neutral-500 mt-2">Try a partial last name, the last 4 of a phone number, or just the street name.</div>
    </section>
  );
}

function SearchingState({ query }) {
  return (
    <section className="rounded-2xl border hairline bg-white p-10 text-center" data-testid="search-loading">
      <div className="inline-flex h-12 w-12 rounded-2xl bg-neutral-100 text-neutral-500 items-center justify-center mb-4 animate-pulse">
        <Search className="h-5 w-5" />
      </div>
      <div className="display text-[20px] font-bold text-neutral-900">Searching for &ldquo;{query}&rdquo;…</div>
      <div className="text-[13px] text-neutral-500 mt-2">Checking the full pipeline across every loan officer&apos;s book.</div>
    </section>
  );
}

function SearchErrorState({ onRetry }) {
  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50/60 p-10 text-center" data-testid="search-error">
      <div className="display text-[18px] font-bold text-neutral-900">The search took too long to answer.</div>
      <div className="text-[13px] text-neutral-600 mt-2">The server may be busy for a moment. Try again.</div>
      <button
        onClick={onRetry}
        data-testid="search-retry-btn"
        className="mt-4 h-9 px-4 rounded-full bg-neutral-900 text-white text-[12.5px] font-semibold hover:bg-black transition-colors"
      >
        Try again
      </button>
    </section>
  );
}

/* ---- Scoring ---- */

function scoreLead(lead, query, digits) {
  const app = lead.application || {};
  let score = 0;
  // Reference ID match is intentionally weighted highest — when a borrower
  // calls in with their mailer ref ID, that exact match must always be the
  // top hit, even above name collisions. Also matches with dashes/spaces
  // stripped so "CONV2026B0388" finds "CONV-2026-B0388".
  if (lead.referenceId) {
    const ref = String(lead.referenceId).toLowerCase();
    const refStripped = ref.replace(/[-_\s]/g, '');
    const qStripped = query.replace(/[-_\s]/g, '');
    if (ref === query) score += 200;
    else if (ref.startsWith(query)) score += 100;
    else if (ref.includes(query)) score += 60;
    else if (qStripped && refStripped.includes(qStripped)) score += 60;
  }
  const fields = [
    [lead.name, 5],
    [app.firstName, 5],
    [app.lastName, 5],
    [lead.email, 3],
    [app.email, 3],
    [lead.source, 3],   // "VA Mailer" → finds all VA leads when LO types "mailer" / "VA"
    [lead.city, 2],
    [app.currentCity, 2],
    [app.currentState, 1],
    [app.currentZip, 2],
    [app.propertyCity, 2],
    [app.propertyState, 1],
    [app.propertyZip, 2],
    [app.employer, 2],
  ];
  for (const [raw, weight] of fields) {
    if (!raw) continue;
    const v = String(raw).toLowerCase();
    if (v === query) score += weight * 3;
    else if (v.startsWith(query)) score += weight * 2;
    else if (v.includes(query)) score += weight;
  }
  // Address fields get directional-aware matching ("W" ↔ "West", "NW" ↔
  // "Northwest", etc.) so a search for "W Yellowstone" still finds a record
  // stored as "West Yellowstone" — and vice versa.
  const addressFields = [
    [app.currentAddress, 4],
    [app.propertyAddress, 4],
  ];
  const nQuery = normalizeAddress(query);
  for (const [raw, weight] of addressFields) {
    if (!raw) continue;
    const nVal = normalizeAddress(raw);
    if (nVal === nQuery) score += weight * 3;
    else if (addressStartsWith(raw, query)) score += weight * 2;
    else if (addressIncludes(raw, query)) score += weight;
  }
  // Phone match only kicks in when the query has at least 3 digits — a
  // 1- or 2-digit fragment like the "4" inside "nw 4th" would otherwise
  // light up every phone number that contains that digit.
  if (digits && digits.length >= 3) {
    for (const raw of [lead.phone, app.phone]) {
      if (!raw) continue;
      const d = String(raw).replace(/\D/g, '');
      if (d.includes(digits)) score += digits.length >= 4 ? 8 : 4;
    }
  }
  return score;
}
